import { useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/use-debounce';
import { useListDriveFolders } from '@/hooks/queries/triggers/use-list-drive-folders';
import { cn } from '@/lib/utils';

interface FolderComboboxProps {
  orgId?: string;
  connectionId: string;
  value: string;
  selectedName: string;
  onSelect: (folderId: string, folderName: string) => void;
  enabled?: boolean;
}

export const FolderCombobox = ({
  orgId,
  connectionId,
  value,
  selectedName,
  onSelect,
  enabled = true,
}: FolderComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Each keystroke would otherwise be a live Drive read through Composio.
  const debouncedSearch = useDebounce(search, 300);

  const foldersQuery = useListDriveFolders(
    orgId,
    connectionId,
    debouncedSearch || undefined,
    enabled,
  );
  const folders = foldersQuery.data?.folders ?? [];
  // Results come from the server already filtered by name.
  const isSearching = foldersQuery.isFetching;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between font-normal'
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ? selectedName || value : 'Search and select a folder'}
          </span>
          <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[--radix-popover-trigger-width] p-0'
        align='start'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Type a folder name…'
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isSearching && folders.length === 0 ? (
              <div className='flex items-center gap-2 px-3 py-6 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Searching your Drive…
              </div>
            ) : (
              <CommandEmpty>
                No folders match. Folders in a Shared Drive are not supported
                yet.
              </CommandEmpty>
            )}
            {folders.map((folder) => {
              const name = folder.name ?? folder.id;
              return (
                <CommandItem
                  key={folder.id}
                  value={folder.id}
                  onSelect={() => {
                    onSelect(folder.id, name);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      folder.id === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className='truncate'>{name}</span>
                  {folder.shared_with_me && (
                    <span className='ml-2 shrink-0 text-xs text-muted-foreground'>
                      shared by {folder.owner_email ?? 'someone else'}
                    </span>
                  )}
                </CommandItem>
              );
            })}
            {foldersQuery.data?.truncated && (
              <p className='px-3 py-2 text-xs text-muted-foreground'>
                Only the most recent folders are shown — keep typing to narrow.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
